import React from "react";

import { DropdownOption, use3dSpatialSoundList } from "../../../../helpers/optionSets";
import { AaaGameInformationTechnicalCapabilitiesDataType } from "../../../../models/forms";
import { ReadOnlyFormItem } from "../appInfoModelsAndHelpers";
import { ReadOnlyForm } from "../ReadOnlyForm";

type ReadOnlyAaaTechnicalCapabilitiesProps = {
  dataInForm: AaaGameInformationTechnicalCapabilitiesDataType;
};

const getCapabilitiesList = (
  localizedStrings: Map<string, string>,
  capabilities: { capability: string; selected: boolean; details: string }[]
) =>
  capabilities
    .filter(value => value.selected === true)
    .map(element => localizedStrings.get(element.capability));

const getCapabilityDetails = (
  capability: string,
  capabilities: { capability: string; selected: boolean; details: string }[]
) =>
  capabilities
    .filter(value => value.selected === true && value.capability === capability)
    .map(element => element.details);

const AaaDistributionInformationConfig: Map<string, ReadOnlyFormItem> = new Map([
  [
    "anticipatedCapabilities",
    {
      mapper: (
        data: AaaGameInformationTechnicalCapabilitiesDataType,
        localizedStrings: Map<string, string>
      ) => getCapabilitiesList(localizedStrings, data.anticipatedCapabilities),
    },
  ],
  [
    "targetNative4k",
    {
      mapper: (data: AaaGameInformationTechnicalCapabilitiesDataType) =>
        getCapabilityDetails("targetNative4k", data.anticipatedCapabilities),
    },
  ],
  [
    "crossGenerationalContent",
    {
      mapper: (data: AaaGameInformationTechnicalCapabilitiesDataType) =>
        getCapabilityDetails("crossGenerationalContent", data.anticipatedCapabilities),
    },
  ],
  [
    "cloudAware",
    {
      mapper: (data: AaaGameInformationTechnicalCapabilitiesDataType) =>
        getCapabilityDetails("cloudAware", data.anticipatedCapabilities),
    },
  ],
  [
    "xboxSeriesXSVersionCapabilities",
    {
      title: "anticipatedXboxSeriesXSCapabilities",
      mapper: (
        data: AaaGameInformationTechnicalCapabilitiesDataType,
        localizedStrings: Map<string, string>
      ) => getCapabilitiesList(localizedStrings, data.xboxSeriesXSVersionCapabilities),
    },
  ],
  [
    "rayTracing",
    {
      mapper: (data: AaaGameInformationTechnicalCapabilitiesDataType) =>
        getCapabilityDetails("rayTracing", data.xboxSeriesXSVersionCapabilities),
    },
  ],
  [
    "highFrameRate",
    {
      mapper: (data: AaaGameInformationTechnicalCapabilitiesDataType) =>
        getCapabilityDetails("highFrameRate", data.xboxSeriesXSVersionCapabilities),
    },
  ],
  [
    "directStorage",
    {
      mapper: (data: AaaGameInformationTechnicalCapabilitiesDataType) =>
        getCapabilityDetails("directStorage", data.xboxSeriesXSVersionCapabilities),
    },
  ],
  [
    "xboxSeriesXSAdvantage",
    {
      title: "seriesXSAdvantageTitle",
    },
  ],
  [
    "xboxSeriesXPerfTargets",
    {
      title: "aaaXboxSeriesXPerfTargetsTitle",
    },
  ],
  [
    "xboxSeriesSPerfTargets",
    {
      title: "aaaXboxSeriesSPerfTargetsTitle",
    },
  ],
  [
    "internetConnectionDetails",
    {
      title: "aaaInternetConnectionDetailsTitle",
    },
  ],
  [
    "spatial3dSound",
    {
      title: "aaa3dSpatialSoundTitle",
      mapper: (
        data: AaaGameInformationTechnicalCapabilitiesDataType,
        _localizedStrings: Map<string, string>,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        additionalData?: Map<string, any>
      ) => {
        if (!additionalData) {
          return null;
        }
        const spatial3dSoundList = additionalData.get("spatial3dSoundList") as DropdownOption[];
        return spatial3dSoundList.find(soundItem => soundItem.value === data.spatial3dSound)?.label;
      },
    },
  ],
]);

export const ReadOnlyAaaTechnicalCapabilities = ({
  dataInForm,
}: ReadOnlyAaaTechnicalCapabilitiesProps) => {
  const extraStringsToLocalize = ["allPlatforms", "hdr"];
  const spatial3dSoundList = use3dSpatialSoundList();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const additionalData = new Map<string, any>([["spatial3dSoundList", spatial3dSoundList]]);
  return (
    <ReadOnlyForm
      formConfig={AaaDistributionInformationConfig}
      formData={dataInForm}
      extraStringsToLocalize={extraStringsToLocalize}
      additionalData={additionalData}
    />
  );
};
